import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

import { cn } from '../lib/cn.js';
import { EDGE_COLORS, NODE_COLORS } from '../lib/graph-constants.js';
import { useUiStore } from '../state/ui-store.js';

const LEGEND_NODES = [
  { label: 'File', kind: 'File' },
  { label: 'Directory', kind: 'Directory' },
  { label: 'Class', kind: 'Class' },
  { label: 'Interface', kind: 'Interface' },
  { label: 'Function', kind: 'Function' },
  { label: 'Method', kind: 'Method' },
  { label: 'Variable', kind: 'Variable' },
  { label: 'Import', kind: 'Import' },
  { label: 'Route', kind: 'Route' },
] as const;

const LEGEND_EDGES = [
  { label: 'Contains', type: 'CONTAINS' },
  { label: 'Defines', type: 'DEFINES' },
  { label: 'Imports', type: 'IMPORTS' },
  { label: 'Calls', type: 'CALLS' },
  { label: 'Inherits', type: 'INHERITS_FROM' },
  { label: 'Implements', type: 'IMPLEMENTS' },
  { label: 'References', type: 'REFERENCES' },
] as const;

export function GraphLegend(): JSX.Element {
  const [open, setOpen] = useState(false);
  const showFileLabels = useUiStore((s) => s.showFileLabels);
  const setShowFileLabels = useUiStore((s) => s.setShowFileLabels);

  return (
    <div className="absolute bottom-3 right-3 z-[4] font-mono text-[10px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg border border-border-subtle bg-surface/90 px-2.5 py-1.5 text-text-secondary shadow-panel backdrop-blur-md transition-colors hover:border-border-default hover:text-text-primary"
      >
        <span className="text-[10px] uppercase tracking-widest">Legend</span>
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
      </button>

      {open && (
        <div className="absolute bottom-8 right-0 w-48 animate-fade-in rounded-xl border border-border-subtle bg-surface/95 p-3 shadow-panel backdrop-blur-md">
          <Section title="Nodes">
            {LEGEND_NODES.map(({ label, kind }) => (
              <Row key={kind} color={NODE_COLORS[kind] ?? '#6b7280'} label={label} dot />
            ))}
          </Section>
          <div className="my-2 h-px bg-border-subtle" />
          <Section title="Edges">
            {LEGEND_EDGES.map(({ label, type }) => (
              <Row key={type} color={EDGE_COLORS[type] ?? '#334155'} label={label} />
            ))}
          </Section>
          <div className="my-2 h-px bg-border-subtle" />
          <Section title="Display">
            <FileLabelsToggle value={showFileLabels} onChange={(v) => setShowFileLabels(v)} />
          </Section>
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="mb-0.5 text-[9px] uppercase tracking-[0.16em] text-text-muted">{title}</span>
      {children}
    </div>
  );
}

function Row({
  color,
  label,
  dot,
}: {
  readonly color: string;
  readonly label: string;
  readonly dot?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center gap-1.5">
      {dot ? (
        <span
          className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
          style={{ background: color, boxShadow: `0 0 4px ${color}60` }}
        />
      ) : (
        <span
          className={cn('h-0.5 w-4 flex-shrink-0 rounded-full')}
          style={{ background: color }}
        />
      )}
      <span className="text-text-secondary">{label}</span>
    </div>
  );
}

function FileLabelsToggle({
  value,
  onChange,
}: {
  readonly value: boolean;
  readonly onChange: (next: boolean) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label="Toggle file labels"
      onClick={() => onChange(!value)}
      className="flex items-center justify-between gap-2 rounded-md px-1 py-0.5 text-[11px] text-text-secondary transition-colors hover:text-text-primary"
    >
      <span>File labels</span>
      <span
        className={cn(
          'inline-flex h-3 w-6 items-center rounded-full border border-border-subtle transition-colors',
          value ? 'bg-accent/60' : 'bg-surface/60',
        )}
      >
        <span
          className={cn(
            'h-2 w-2 rounded-full bg-text-primary transition-transform',
            value ? 'translate-x-3' : 'translate-x-0.5',
          )}
        />
      </span>
    </button>
  );
}
